package es.subastia.auctionlist

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import es.subastia.domain.model.AuctionProcedureType
import es.subastia.domain.model.AuctionTypeDomainModel
import es.subastia.domain.model.ProvinceDomainModel
import es.subastia.domain.model.SavedSearchModel
import es.subastia.domain.usecase.DeleteSearchUseCase
import es.subastia.domain.usecase.GetAuctionsUseCase
import es.subastia.domain.usecase.GetSavedSearchesUseCase
import es.subastia.domain.usecase.GetUserPreferencesUseCase
import es.subastia.domain.usecase.ObserveAuthStateUseCase
import es.subastia.navigation.NavRoute
import es.subastia.domain.usecase.SaveSearchUseCase
import es.subastia.domain.usecase.SaveUserPreferencesUseCase
import es.subastia.ui.mapper.toUi
import es.subastia.ui.model.AuctionUiModel
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.firstOrNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.getString
import subastia.composeapp.generated.resources.Res
import subastia.composeapp.generated.resources.default_saved_search_title
enum class SortOrder {
    NEWEST, OLDEST
}

class AuctionListViewModel(
    getUserPreferencesUseCase: GetUserPreferencesUseCase,
    private val getAuctionsUseCase: GetAuctionsUseCase,
    private val observeAuthStateUseCase: ObserveAuthStateUseCase,
    private val saveUserPreferencesUseCase: SaveUserPreferencesUseCase,
    private val saveSearchUseCase: SaveSearchUseCase,
    private val getSavedSearchesUseCase: GetSavedSearchesUseCase,
    private val deleteSearchUseCase: DeleteSearchUseCase,
) : ViewModel() {
    private val _sortOrder = MutableStateFlow(SortOrder.NEWEST)

    private val _searchQuery = MutableStateFlow("")
    val currentSearchQuery: StateFlow<String> = _searchQuery.asStateFlow()

    private val _isRefreshing = MutableStateFlow(false)
    val isRefreshing: StateFlow<Boolean> = _isRefreshing.asStateFlow()

    private val _refreshTrigger = MutableStateFlow(0)

    private val prefsFlow = combine(
        getUserPreferencesUseCase.getSavedType(),
        getUserPreferencesUseCase.getSavedProcedureType(),
        getUserPreferencesUseCase.getSavedProvince(),
    ) { type, procedureType, province -> Triple(type, procedureType, province) }

    val uiState: StateFlow<AuctionUiState> = combine(
        prefsFlow,
        _sortOrder,
        _searchQuery,
        _refreshTrigger,
        observeAuthStateUseCase().map { it != null }.distinctUntilChanged(),
    ) { args ->
        @Suppress("UNCHECKED_CAST")
        val triple = args[0] as Triple<AuctionTypeDomainModel?, AuctionProcedureType?, ProvinceDomainModel?>
        val type = triple.first
        val procedureType = triple.second
        val province = triple.third

        val sortOrder = args[1] as SortOrder
        val query = args[2] as String
        val isLoggedIn = args[4] as Boolean

        _isRefreshing.value = true
        try {
            val auctions = getAuctionsUseCase(type, procedureType, province, query.takeIf { it.isNotBlank() }).map { it.toUi() }

            val sortedAuctions = when (sortOrder) {
                SortOrder.NEWEST -> auctions.sortedByDescending { it.dates.startDate ?: it.auctionId }
                SortOrder.OLDEST -> auctions.sortedBy { it.dates.startDate ?: it.auctionId }
            }

            AuctionUiState.Success(
                list = sortedAuctions,
                selectedType = type?.text,
                selectedProcedureType = procedureType,
                selectedProvince = province?.nm,
                sortOrder = sortOrder,
                searchQuery = query,
                isLoggedIn = isLoggedIn,
            ) as AuctionUiState
        } finally {
            _isRefreshing.value = false
        }
    }
        .catch { emit(AuctionUiState.Error(it.message.toString())) }
        .stateIn(
            scope = viewModelScope,
            initialValue = AuctionUiState.Loading,
            started = SharingStarted.WhileSubscribed(5_000),
        )
    
    @OptIn(ExperimentalCoroutinesApi::class)
    val isSearchSaved: StateFlow<Boolean> = combine(
        observeAuthStateUseCase(),
        uiState,
    ) { currentUser, state ->
        val currentState = state as? AuctionUiState.Success
        if (currentUser == null || currentState == null) {
            flowOf(false)
        } else {
             getSavedSearchesUseCase(currentUser.uid).map { savedSearches ->
                savedSearches.any { saved ->
                    saved.query == currentState.searchQuery &&
                    saved.type?.text == currentState.selectedType &&
                    saved.procedureType == currentState.selectedProcedureType &&
                    saved.province?.nm == currentState.selectedProvince
                }
            }
        }
    }.flatMapLatest { it }
    .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), false)

    var scrollIndex = 0
        private set
    var scrollOffset = 0
        private set

    fun refresh() {
        _refreshTrigger.value += 1
    }

    fun onSearchQueryChanged(query: String) {
        _searchQuery.value = query
    }

    fun onRemoveProvince() {
        viewModelScope.launch {
            saveUserPreferencesUseCase.saveProvince(null)
        }
    }

    fun onRemoveType() {
        viewModelScope.launch {
            saveUserPreferencesUseCase.saveType(null)
        }
    }

    fun onRemoveProcedureType() {
        viewModelScope.launch {
            saveUserPreferencesUseCase.saveProcedureType(null)
        }
    }

    fun onFilterChanged(newFilter: String) {
        // Will be implemented with Filter Screen navigation interaction
    }

    fun toggleSortOrder() {
        _sortOrder.update {
            if (it == SortOrder.NEWEST) SortOrder.OLDEST else SortOrder.NEWEST
        }
    }

    fun saveScrollPosition(index: Int, offset: Int) {
        scrollIndex = index
        scrollOffset = offset
    }

    fun loadSearchFromParams(params: NavRoute.AuctionList) {
        viewModelScope.launch {
            // 1. Set Query
            _searchQuery.value = params.query ?: ""

            // 2. Set Type
            val typeDomainModel = params.type?.let { AuctionTypeDomainModel.fromString(it) }
            saveUserPreferencesUseCase.saveType(typeDomainModel)

            // 3. Set Procedure Type
            val procedureTypeDomainModel = params.procedureType?.let {
                // Assuming we passed 'name' (enum constant name)
                try {
                    AuctionProcedureType.valueOf(it)
                } catch (e: IllegalArgumentException) {
                    // Fallback if we passed label or something else
                    AuctionProcedureType.fromString(it)
                }
            }
            saveUserPreferencesUseCase.saveProcedureType(procedureTypeDomainModel)

            // 4. Set Province
            val provinceDomainModel = params.province?.let {
                ProvinceDomainModel.fromString(it)
            }
            saveUserPreferencesUseCase.saveProvince(provinceDomainModel)

            // Trigger refresh if needed, but flows might auto-trigger if they observe these values.
            // combine(...) logic usually handles it.
            // However, 'refresh()' might be needed to reset paging.
            refresh()
        }
    }

    fun toggleSavedSearch() {
        viewModelScope.launch {
            val currentUser = observeAuthStateUseCase().firstOrNull() ?: return@launch
            val currentState = uiState.value as? AuctionUiState.Success ?: return@launch

            val existingSearch = getSavedSearchesUseCase(currentUser.uid).firstOrNull()?.find { saved ->
                saved.query == currentState.searchQuery &&
                saved.type?.text == currentState.selectedType &&
                saved.procedureType == currentState.selectedProcedureType &&
                saved.province?.nm == currentState.selectedProvince
            }

            if (existingSearch != null) {
                deleteSearchUseCase(existingSearch.id, currentUser.uid)
            } else {
                saveCurrentSearch()
            }
        }
    }

    private suspend fun saveCurrentSearch() {
        val currentUser = observeAuthStateUseCase().firstOrNull() ?: return
        val currentState = uiState.value as? AuctionUiState.Success ?: return

        val title = buildString {
            if (currentState.searchQuery.isNotBlank()) append(currentState.searchQuery)
            currentState.selectedType?.let {
                if (isNotEmpty()) append(" - ")
                append(it)
            }
            currentState.selectedProvince?.let {
                if (isNotEmpty()) append(" - ")
                append(it)
            }
            if (isEmpty()) append(getString(Res.string.default_saved_search_title))
        }

        val savedSearch = SavedSearchModel(
            userId = currentUser.uid,
            title = title,
            query = currentState.searchQuery,
            type = currentState.selectedType?.let { AuctionTypeDomainModel.fromString(it) },
            procedureType = currentState.selectedProcedureType,
            province = currentState.selectedProvince?.let { ProvinceDomainModel.fromString(it) },
            createdAt = 0L // Placeholder
        )
            saveSearchUseCase(savedSearch)
    }
}

sealed class AuctionUiState {
    object Loading : AuctionUiState()
    data class Success(
        val list: List<AuctionUiModel>,
        val selectedType: String? = null,
        val selectedProcedureType: AuctionProcedureType? = null,
        val selectedProvince: String? = null,
        val sortOrder: SortOrder = SortOrder.NEWEST,
        val searchQuery: String = "",
        val isLoggedIn: Boolean = false
    ) : AuctionUiState()

    data class Error(val message: String) : AuctionUiState()
}
